import React from 'react';

import { Box, Flex, Space } from '@mantine/core';
import { useForm } from '@mantine/form';
import { IconCheck } from '@tabler/icons-react';
import { BubbleButton } from 'components/SmartAssistantComponents/BubbleButton';
import { BubbleTextarea } from 'components/SmartAssistantComponents/BubbleTextarea';
import { BubbleTextInput } from 'components/SmartAssistantComponents/BubbleTextInput';
import { MessageBubble, MessageBubbleProps } from 'components/SmartAssistantComponents/MessageBubble';
import { ViewMode } from 'shared/enums/ViewMode';
import { EmailHandoffToolEventData, ForcedToolInvocation } from 'shared/interfaces/ChatMessage';
import { InfoType, InfoTypeMapping } from 'shared/interfaces/tools/CollectLeadsInfo';
import { EmailHandoffToolConfig } from 'shared/interfaces/tools/EmailHandoff';
import { ToolConfig, ToolKind, ToolTrigger } from 'shared/interfaces/tools/Tool';
import { DEFAULT_MAX_LENGTH, emailValidator, requiredInputValidator } from 'shared/utils/form-validators';

interface EmailHandoffFormBubbleProps extends MessageBubbleProps {
  toolConfigs?: ToolConfig[];
  forceToolInvocation?: (forcedToolInvocation: ForcedToolInvocation) => void;
  viewMode: ViewMode;
  formFilledData?: EmailHandoffToolEventData;
}

export function EmailHandoffFormBubble({
  toolConfigs,
  forceToolInvocation,
  viewMode,
  formFilledData,
  ...messageBubbleProps
}: EmailHandoffFormBubbleProps) {
  // If is live and formFilledData is provided, then we want to hide the form as it has already been filled
  // When not live, we want to show the form with the prefilled data and disabled inputs
  if (viewMode === 'live' && formFilledData) return null;

  const emailHandoffToolConfig = toolConfigs?.find((toolConfig) => toolConfig.kind === ToolKind.EMAIL_HANDOFF) as
    | EmailHandoffToolConfig
    | undefined;

  if (!emailHandoffToolConfig) return null;

  const { email, name, phone, reason } = formFilledData ?? {};

  const emailHandoffForm = useForm({
    initialValues: {
      email: viewMode !== 'live' && email ? email : '',
      name: viewMode !== 'live' && name ? name : '',
      phone: viewMode !== 'live' && phone ? phone : '',
      reason: viewMode !== 'live' && reason ? reason : ''
    },
    validate: {
      email: (value) =>
        emailHandoffToolConfig.infoTypesToCollect.includes(InfoType.EMAIL)
          ? requiredInputValidator(value) || emailValidator(value)
          : null,
      name: (value) =>
        emailHandoffToolConfig.infoTypesToCollect.includes(InfoType.NAME) ? requiredInputValidator(value) : null,
      phone: (value) =>
        emailHandoffToolConfig.infoTypesToCollect.includes(InfoType.PHONE) ? requiredInputValidator(value) : null,
      reason: (value) =>
        emailHandoffToolConfig.infoTypesToCollect.includes(InfoType.REASON) ? requiredInputValidator(value) : null
    }
  });

  const getInfoTypes = () => {
    const infoTypes = [];
    // If the form is not live and formFilledData is provided, we want to show the filled data
    // This case is for when we show the form filled in the conversation viewer.
    if (viewMode !== 'live' && formFilledData) {
      for (const key in formFilledData) {
        const infoTypeFromKey = InfoTypeMapping[key];
        if (Object.prototype.hasOwnProperty.call(formFilledData, key) && infoTypeFromKey) {
          infoTypes.push(infoTypeFromKey);
        }
      }
    } else {
      infoTypes.push(...emailHandoffToolConfig.infoTypesToCollect);
    }

    return infoTypes;
  };

  return (
    <MessageBubble
      maw={375}
      miw={280}
      message={{ text: emailHandoffToolConfig?.title, fromUser: false }}
      {...messageBubbleProps}>
      <Flex direction="column" align="flex-start" mt="xs">
        {getInfoTypes().map((infoType) => (
          <Box key={infoType} w="100%">
            {infoType === InfoType.REASON ? (
              <BubbleTextarea
                smartAssistantVisualConfig={messageBubbleProps.smartAssistantVisualConfig}
                maxLength={DEFAULT_MAX_LENGTH}
                disabled={viewMode === 'readonly'}
                label={emailHandoffToolConfig.labels[infoType] || ''}
                minRows={4}
                maxRows={4}
                {...emailHandoffForm.getInputProps(infoType)}
              />
            ) : (
              <BubbleTextInput
                smartAssistantVisualConfig={messageBubbleProps.smartAssistantVisualConfig}
                maxLength={DEFAULT_MAX_LENGTH}
                disabled={viewMode === 'readonly'}
                label={emailHandoffToolConfig.labels[infoType] || ''}
                {...emailHandoffForm.getInputProps(infoType)}
              />
            )}

            <Space h={10} />
          </Box>
        ))}

        <Space h={10} />
        <BubbleButton
          smartAssistantVisualConfig={messageBubbleProps.smartAssistantVisualConfig}
          fullWidth
          disabled={viewMode === 'readonly'}
          onClick={() => {
            if (emailHandoffToolConfig && viewMode === 'live' && forceToolInvocation) {
              const leadFormValidation = emailHandoffForm.validate();
              if (leadFormValidation.hasErrors) return;

              const { email, name, phone, reason } = emailHandoffForm.values;

              forceToolInvocation({
                config: {
                  kind: ToolKind.COMPLETE_EMAIL_HANDOFF,
                  toolName: ToolKind.COMPLETE_EMAIL_HANDOFF,
                  enabled: true,
                  trigger: ToolTrigger.FORCED,
                  conversationId: '', // Will be filled by the parent component
                  email: email,
                  name: name === '' ? undefined : name,
                  phone: phone === '' ? undefined : phone,
                  reason: reason === '' ? undefined : reason
                }
              } as ForcedToolInvocation);
            }
          }}>
          <IconCheck />
        </BubbleButton>
      </Flex>
    </MessageBubble>
  );
}
